import { Trans } from '@lingui/react/macro';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import { EntityGroup } from '@/models/entityGroup';

export const GROUPS_PER_PAGE = 20;

const MAX_ENTITIES_SHOWN_PER_GROUP = 10;

interface GroupsTableProps {
  entityGroups: EntityGroup[];
  isLoading: boolean;
  isError: boolean;
  page: number;
  onPageChange: (page: number) => void;
  count: number | undefined;
}

export const GroupsTable = ({
  entityGroups,
  isLoading,
  isError,
  page,
  onPageChange,
  count,
}: GroupsTableProps) => {
  return (
    <TableQueryWrapper isLoading={isLoading} isError={isError}>
      {entityGroups.length === 0 ? (
        <EmptyState
          subtitle={
            <Stack>
              <Paragraph>
                <Trans id="settings.organization.entities.groups.no-results.1">
                  No results are matching your criteria.
                </Trans>
              </Paragraph>
              <Paragraph>
                <Trans id="settings.organization.entities.groups.no-results.2">
                  Please try with different keywords.
                </Trans>
              </Paragraph>
            </Stack>
          }
          illustration={<IllustrationNoResults />}
        />
      ) : (
        <Table columnWidths={['1fr', '1fr']} bordered>
          <Header>
            <Row>
              <Column>
                <Trans id="settings.organization.entities.groups.table.header.name">Name</Trans>
              </Column>
              <Column>
                <Trans id="settings.organization.entities.groups.table.header.entities">
                  Entities
                </Trans>
              </Column>
            </Row>
          </Header>

          <Body>
            {entityGroups.map((group) => (
              <GroupRow key={group.id} group={group} />
            ))}
          </Body>

          <PaginationFooter
            itemsPerPage={GROUPS_PER_PAGE}
            page={page}
            onPageChange={onPageChange}
            count={count}
          />
        </Table>
      )}
    </TableQueryWrapper>
  );
};

const GroupRow = ({ group }: { group: EntityGroup }) => {
  const entitiesLabel =
    group.entities
      .slice(0, MAX_ENTITIES_SHOWN_PER_GROUP)
      .map((entity) => entity.name)
      .join(', ') || '-';

  return (
    <Row to={`/settings_beta/network/groups/${group.id}`}>
      <Cell isLink>
        <Paragraph weight="medium">{group.name}</Paragraph>
      </Cell>
      <Cell>
        <Paragraph size="200">
          {entitiesLabel}
          {group.entities.length > MAX_ENTITIES_SHOWN_PER_GROUP && (
            <>, +{group.entities.length - MAX_ENTITIES_SHOWN_PER_GROUP}</>
          )}
        </Paragraph>
      </Cell>
    </Row>
  );
};
